import React, { useState, useEffect } from 'react';
import ReactQueryProvider from './ReactQueryProvider';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import ComingSoonPage from './pages/ComingSoonPage';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import BookingPage from './pages/BookingPage';
import ProductsPage from './pages/ProductsPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ConfirmationPage from './pages/ConfirmationPage';
import PricingPage from './pages/PricingPage';
import WebshopPage from './pages/WebshopPage';
import DevApiPage from './pages/DevApiPage';
import ContactPage from './pages/ContactPage';
import MyBookingsPage from './pages/MyBookingsPage';
import MyOrdersPage from './pages/MyOrdersPage';
import MyAccountPage from './pages/MyAccountPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProtectedRoute from './components/ProtectedRoute';
import AuthTopbar from './components/AuthTopbar';
import ErrorBoundary from './components/ErrorBoundary';
import BackToTop from './components/BackToTop';
import { Toaster } from './components/ui/toaster';
import { ShoppingCart, Menu } from 'lucide-react';
import { Badge } from './components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './components/ui/sheet';

function App() {
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(null);
  
  // Maintenance mode - set to true to enable Coming Soon page only
  const MAINTENANCE_MODE = true;

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity === 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // If maintenance mode is on, show only Coming Soon page
  if (MAINTENANCE_MODE) {
    return (
      <Router>
        <ErrorBoundary>
          <ReactQueryProvider>
            <div className="App min-h-screen bg-[#F8FAFB]">
              <Routes>
                <Route path="*" element={<ComingSoonPage />} />
              </Routes>
              <Toaster />
            </div>
          </ReactQueryProvider>
        </ErrorBoundary>
      </Router>
    );
  }

  return (
    <Router>
      <ErrorBoundary>
        <ReactQueryProvider>
          <div className="App min-h-screen bg-[#F8FAFB]">
          <Navigation cartCount={cartCount} />
          {/* Auth topbar þegar notandi er innskráður */}
          {typeof window !== 'undefined' && localStorage.getItem('auth_token') && <AuthTopbar />}
          <main>
            <Routes>
              <Route path="/" element={<ComingSoonPage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/booking" element={<BookingPage user={user} setUser={setUser} />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/webshop" element={<WebshopPage addToCart={addToCart} />} />
              {/* Redirect gamla slóðin yfir á Mitt svæði til að forðast rugling */}
              <Route path="/my-bookings" element={<Navigate to="/my-account" replace />} />
              <Route path="/my-orders" element={<Navigate to="/my-account" replace />} />


              <Route path="/products" element={<ProductsPage addToCart={addToCart} />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/cart" element={<CartPage cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} />} />
              <Route path="/checkout" element={<CheckoutPage cart={cart} clearCart={clearCart} user={user} setUser={setUser} />} />
              <Route path="/confirmation" element={<ConfirmationPage />} />
              <Route
                path="/my-account"
                element={(
                  <ProtectedRoute>
                    <MyAccountPage />
                  </ProtectedRoute>
                )}
              />
              {/* Legacy routes halda áfram að virka ef einhverjir hafa bókamerkt þær */}
              <Route
                path="/my-bookings"
                element={(
                  <ProtectedRoute>
                    <MyBookingsPage user={user} />
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/my-orders"
                element={(
                  <ProtectedRoute>
                    <MyOrdersPage user={user} />
                  </ProtectedRoute>
                )}
              />
              <Route path="/checkout/mock" element={<MockPaymentPage clearCart={clearCart} />} />
              {/* Dev route - ekki sýnilegt í navigation */}
              <Route path="/dev/api" element={<DevApiPage />} />
            </Routes>
          </main>
          <Footer />
          <BackToTop />
          <Toaster />
        </div>
        </ReactQueryProvider>
      </ErrorBoundary>
    </Router>
  );
}

function Navigation({ cartCount }) {
  const navigate = useNavigate();
  
  return (
    <nav className="bg-white border-b border-[#E8ECEF] sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl font-bold text-[#0A4D8C]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Alvörubón
            </div>
          </Link>
          
          {/* Desktop navigation */}
          <div className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium">
              Heim
            </Link>
            <Link to="/services" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium">
              Þjónusta
            </Link>
            <Link to="/booking" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium">
              Bóka Tíma
            </Link>
            <Link to="/pricing" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium">
              Verðskrá
            </Link>
            <Link to="/products" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium">
              Verslun
            </Link>
            <Link to="/my-account" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium" data-testid="nav-mitt-svaedi">
              Mitt svæði
            </Link>
            <Link to="/contact" className="text-[#2C3E50] hover:text-[#0A4D8C] transition-colors font-medium">
              Hafa Samband
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile menu */}
            <Sheet>
              <SheetTrigger className="md:hidden p-2 rounded-lg text-[#0A4D8C] hover:bg-[#F8FAFB]" aria-label="Opna valmynd">
                <Menu className="h-6 w-6" />
              </SheetTrigger>
              <SheetContent side="right" className="w-64">
                <nav className="mt-8 space-y-4">
                  <SheetClose asChild>
                    <Link to="/" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-heim">
                      Heim
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/services" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-thjonusta">
                      Þjónusta
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/booking" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-boka">
                      Bóka Tíma
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/pricing" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-verdsfra">
                      Verðskrá
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/products" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-verslun">
                      Verslun
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/my-account" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-mitt-svaedi">
                      Mitt svæði
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/contact" className="block text-[#2C3E50] hover:text-[#0A4D8C] font-semibold text-lg" data-testid="nav-mobile-contact">
                      Hafa Samband
                    </Link>
                  </SheetClose>
                </nav>
              </SheetContent>
            </Sheet>

            <button 
              onClick={() => navigate('/cart')}
              className="relative p-2 text-[#0A4D8C] hover:bg-[#F8FAFB] rounded-lg transition-colors"
              data-testid="cart-button"
            >
              <ShoppingCart className="h-6 w-6" />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-[#EF4444] text-white text-xs">
                  {cartCount}
                </Badge>
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1A252F] text-white mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Alvörubón ehf.
            </h3>
            <p className="text-sm text-[#8B9BA8] mb-2">Kt. 620318-0350</p>
            <p className="text-sm text-[#E8ECEF] leading-relaxed mb-4">
              Fagleg bílaþjónusta, þvottur og bónvinna með yfir 15 ára reynslu.
            </p>
            <div className="flex gap-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#2C3E50] hover:bg-[#0A4D8C] rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-white"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M13.5 9H15V6.75h-1.5c-1.657 0-3 1.343-3 3V12H8v2.25h3v6h2.25v-6h3L16.5 12h-3v-2.25c0-.414.336-.75.75-.75Z"
                  />
                </svg>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#2C3E50] hover:bg-[#0A4D8C] rounded-full flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-white"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Zm0 7.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm4.75-7.875a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0Z"
                  />
                  <path
                    fill="currentColor"
                    d="M7.5 4h9A3.5 3.5 0 0 1 20 7.5v9A3.5 3.5 0 0 1 16.5 20h-9A3.5 3.5 0 0 1 4 16.5v-9A3.5 3.5 0 0 1 7.5 4Zm0 1.5A2 2 0 0 0 5.5 7.5v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-9Z"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Flýtileiðir</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/services" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Þjónusta
                </Link>
              </li>
              <li>
                <Link to="/booking" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Bóka tíma
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Verðskrá
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Verslun
                </Link>
              </li>
              <li>
                <Link to="/my-bookings" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Mínar bókanir
                </Link>
              </li>
              <li>
                <Link to="/my-orders" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Mínar pantanir
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#8B9BA8] hover:text-white transition-colors flex items-center gap-2">
                  <span className="text-[#0A4D8C]">→</span> Hafa samband
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Hafðu samband</h4>
            <ul className="space-y-3 text-sm" data-testid="company-contact">
              <li className="flex items-start gap-2">
                <span className="text-[#0A4D8C]" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M6.62 5.5a1 1 0 0 1 .91-.5h2.03a1 1 0 0 1 .98.8l.7 3.51a1 1 0 0 1-.46 1.02l-1.27.85a11.05 11.05 0 0 0 4.7 4.7l.85-1.27a1 1 0 0 1 1.02-.46l3.51.7a1 1 0 0 1 .8.98v2.03a1 1 0 0 1-.5.91l-2.25 1.3a2 2 0 0 1-1.95.02c-2.9-1.52-5.26-3.88-6.78-6.78a2 2 0 0 1 .02-1.95Z" />
                  </svg>
                </span>
                <div>
                  <p className="text-[#E8ECEF] font-medium">Aðal sími</p>
                  <a href="tel:5880705" className="text-[#8B9BA8] hover:text-white transition-colors">
                    588-0705
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F59E0B]" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M11.05 2.52a1.5 1.5 0 0 1 1.9 0l.16.13 7.1 7.1a1.5 1.5 0 0 1 .16 1.93l-.11.14-7.1 7.1a1.5 1.5 0 0 1-1.93.16l-.14-.11-7.1-7.1a1.5 1.5 0 0 1-.16-1.93l.11-.14 7.1-7.1Zm.95 3.48-4.24 4.24L12 13.48 16.24 9.24 12 6Z" />
                  </svg>
                </span>
                <div>
                  <p className="text-[#E8ECEF] font-medium">Neyðarsími</p>
                  <a href="tel:7835000" className="text-[#8B9BA8] hover:text-white transition-colors">
                    783-5000
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0A4D8C]" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M4 4h16a2 2 0 0 1 2 2v.35l-10 6.25L2 6.35V6a2 2 0 0 1 2-2Zm18 5.15-9.35 5.84a1 1 0 0 1-1.03 0L2 9.15V18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2Z" />
                  </svg>
                </span>
                <div>
                  <p className="text-[#E8ECEF] font-medium">Netfang</p>
                  <a href="mailto:alvorubon@alvorubon.is" className="text-[#8B9BA8] hover:text-white transition-colors">
                    alvorubon@alvorubon.is
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#8B5CF6]" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M6 4h12a2 2 0 0 1 2 2v2.5A3.5 3.5 0 0 1 16.5 12H16v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm14 4.5V6h-5v4h1.5a1.5 1.5 0 0 0 1.5-1.5ZM8 9a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H8Zm0 4a1 1 0 1 0 0 2h3a1 1 0 1 0 0-2H8Z" />
                  </svg>
                </span>
                <div>
                  <p className="text-[#E8ECEF] font-medium">Reikningar</p>
                  <a href="mailto:reikningar@alvorubon.is" className="text-[#8B9BA8] hover:text-white transition-colors">
                    reikningar@alvorubon.is
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Location & Hours */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Opnunartímar</h4>
            <ul className="space-y-2 text-sm mb-6">
              <li className="flex justify-between">
                <span className="text-[#8B9BA8]">Mán - Fös</span>
                <span className="text-[#E8ECEF] font-medium">09:00 - 18:00</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#8B9BA8]">Laugardagur</span>
                <span className="text-[#E8ECEF] font-medium">10:00 - 16:00</span>
              </li>
              <li className="flex justify-between">
                <span className="text-[#8B9BA8]">Sunnudagur</span>
                <span className="text-[#EF4444] font-medium">Lokað</span>
              </li>
            </ul>
            <div className="text-sm" data-testid="company-location">
              <p className="text-[#E8ECEF] font-medium mb-1">📍 Staðsetning</p>
              <p className="text-[#8B9BA8]">
                Viðarhöfða 2a<br />
                110 Reykjavík
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#2C3E50] mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#8B9BA8] text-sm">
              &copy; 2025 Alvörubón ehf. Allur réttur áskilinn.
            </p>
            <div className="flex gap-6 text-sm text-[#8B9BA8]">
              <a href="#" className="hover:text-white transition-colors">Persónuvernd</a>
              <a href="#" className="hover:text-white transition-colors">Skilmálar</a>
              <Link to="/contact" className="hover:text-white transition-colors">Hafa samband</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function MockPaymentPage({ clearCart }) {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get('order_id');

  useEffect(() => {
    const completePayment = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          clearCart();
          navigate('/confirmation?type=order&id=' + orderId);
        }
      } catch (error) {
        console.error('Payment error:', error);
      }
    };
    
    completePayment();
  }, [orderId, clearCart, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A4D8C] mx-auto mb-4"></div>
        <p className="text-lg text-[#2C3E50]">Vinnsla greiðslu...</p>
      </div>
    </div>
  );
}

export default App;
